/**
 * Defining a field, in place, with a back link - no modal.
 *
 * The key preview is the important bit of this screen. A key is permanent, so
 * it is shown live as the label is typed, next to a plain sentence saying it
 * cannot change afterwards. Getting that decision in front of the author while
 * they can still act on it is cheaper than any migration later.
 */
import { useMemo, useState, type FormEvent } from "react";
import { DATA_TYPES, DOMAIN_TYPES, NUMERIC_TYPES, deriveFieldKey, type DataType } from "@dp/shared";
import { ApiError, api } from "../../lib/api.ts";
import { useRouter } from "../../lib/router.tsx";
import { useAsync } from "../../lib/useAsync.ts";
import { DATA_TYPE_LABELS, type DomainRow, type EntityRow, type FieldReference, type FieldRow } from "../../lib/types.ts";
import { BackLink, Loading, PageHead, Problem } from "../../components/Page.tsx";

export function FieldEditor({ fieldKey }: { fieldKey?: string }) {
  const editing = Boolean(fieldKey);
  const entities = useAsync(() => api.get<{ entities: EntityRow[] }>("/api/entities"), []);
  const domains = useAsync(() => api.get<{ domains: DomainRow[] }>("/api/domains"), []);
  const existing = useAsync(
    () => (fieldKey ? api.get<{ field: FieldRow; references: FieldReference[] }>(`/api/fields/${fieldKey}`) : Promise.resolve(null)),
    [fieldKey],
  );

  if (entities.loading || domains.loading || (editing && existing.loading)) return <Loading what="the field" />;
  if (editing && existing.error) return <Problem error={existing.error} />;

  return (
    <Form
      entities={entities.data?.entities ?? []}
      domains={domains.data?.domains ?? []}
      field={existing.data?.field ?? null}
      references={existing.data?.references ?? []}
    />
  );
}

function Form({
  entities, domains, field, references,
}: {
  entities: EntityRow[];
  domains: DomainRow[];
  field: FieldRow | null;
  references: FieldReference[];
}) {
  const { navigate } = useRouter();
  const editing = Boolean(field);
  const locked = references.length > 0;

  const [label, setLabel] = useState(field?.label ?? "");
  const [entityKey, setEntityKey] = useState(field?.entityKey ?? entities[0]?.key ?? "");
  const [dataType, setDataType] = useState<DataType>(field?.dataType ?? "text");
  const [valueDomainKey, setValueDomainKey] = useState(field?.valueDomainKey ?? "");
  const [helpText, setHelpText] = useState(field?.helpText ?? "");
  const [active, setActive] = useState(field?.active ?? true);
  const [validation, setValidation] = useState(() => ({
    min: field?.validation?.min ?? "",
    max: field?.validation?.max ?? "",
    minLength: field?.validation?.minLength ?? "",
    maxLength: field?.validation?.maxLength ?? "",
    pattern: field?.validation?.pattern ?? "",
    precision: field?.validation?.precision ?? "",
  }));
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const needsDomain = DOMAIN_TYPES.includes(dataType);
  const isNumeric = NUMERIC_TYPES.includes(dataType);
  const isTextual = ["text", "long_text", "email", "phone"].includes(dataType);

  const previewKey = useMemo(
    () => (field ? field.key : label.trim() && entityKey ? deriveFieldKey(entityKey, label) : ""),
    [field, label, entityKey],
  );

  async function submit(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    setBusy(true);

    const numeric = (value: string | number) => (value === "" ? null : Number(value));
    const payload = {
      label: label.trim(),
      entityKey,
      dataType,
      valueDomainKey: needsDomain ? valueDomainKey || null : null,
      helpText: helpText.trim() || null,
      validation: {
        min: isNumeric ? numeric(validation.min) : null,
        max: isNumeric ? numeric(validation.max) : null,
        precision: isNumeric && dataType !== "integer" ? numeric(validation.precision) : null,
        minLength: isTextual ? numeric(validation.minLength) : null,
        maxLength: isTextual ? numeric(validation.maxLength) : null,
        pattern: isTextual ? validation.pattern.trim() || null : null,
      },
    };

    try {
      if (field) {
        await api.patch(`/api/fields/${field.key}`, { ...payload, active });
      } else {
        await api.post("/api/fields", payload);
      }
      navigate("/fields");
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "Could not save this field.");
      setBusy(false);
    }
  }

  async function remove() {
    setProblem(null);
    try {
      await api.del(`/api/fields/${field!.key}`);
      navigate("/fields");
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "Could not delete this field.");
    }
  }

  if (entities.length === 0) {
    return (
      <div className="stack">
        <BackLink to="/fields">Field library</BackLink>
        <div className="notice notice-warn">
          Define an entity first — a field has to describe something.
        </div>
      </div>
    );
  }

  return (
    <div className="stack">
      <BackLink to="/fields">Field library</BackLink>
      <PageHead
        title={field ? field.label : "New field"}
        sub={field ? <span className="key">{field.key}</span> : "Define it once here and any product can ask it."}
      />

      {problem && <div className="notice notice-bad">{problem}</div>}

      {locked && (
        <div className="notice notice-info">
          Used by {references.map((reference) => `${reference.productName} v${reference.version}`).join(", ")}. The wording
          and the help text can still change; what the field <em>is</em> cannot, while a product is asking for it.
        </div>
      )}

      <form className="card" onSubmit={submit}>
        <div className="card-body stack">
          <div className="field-row">
            <div className="field">
              <label htmlFor="label">Label<span className="required-mark">*</span></label>
              <input
                id="label" className="control" value={label} required autoFocus
                onChange={(event) => setLabel(event.target.value)}
                placeholder="Year built"
              />
              <span className="hint">What the person filling the form reads.</span>
            </div>

            <div className="field">
              <label htmlFor="entity">Describes<span className="required-mark">*</span></label>
              <select
                id="entity" className="control" value={entityKey} disabled={Boolean(field)}
                onChange={(event) => setEntityKey(event.target.value)}
              >
                {entities.map((entity) => (
                  <option key={entity.key} value={entity.key}>
                    {entity.label}{entity.repeating ? " (many)" : ""}
                  </option>
                ))}
              </select>
              <span className="hint">{field ? "Fixed once the field exists." : "The thing this field is about."}</span>
            </div>
          </div>

          {previewKey && (
            <div className={`notice ${field ? "notice-info" : "notice-warn"}`}>
              <strong>Key:</strong> <span className="key">{previewKey}</span>
              <div className="small" style={{ marginTop: 4 }}>
                {field
                  ? "Permanent. Every product and every stored answer refers to the field by this."
                  : "This is permanent — every answer ever stored will be filed under it. The label can change later; this cannot."}
              </div>
            </div>
          )}

          <div className="field-row">
            <div className="field">
              <label htmlFor="type">Type<span className="required-mark">*</span></label>
              <select
                id="type" className="control" value={dataType} disabled={locked}
                onChange={(event) => setDataType(event.target.value as DataType)}
              >
                {DATA_TYPES.map((type) => (
                  <option key={type} value={type}>{DATA_TYPE_LABELS[type]}</option>
                ))}
              </select>
              <span className="hint">Decides the control drawn on the form and how the answer is checked.</span>
            </div>

            {needsDomain && (
              <div className="field">
                <label htmlFor="domain">Values come from<span className="required-mark">*</span></label>
                <select
                  id="domain" className="control" value={valueDomainKey} required disabled={locked}
                  onChange={(event) => setValueDomainKey(event.target.value)}
                >
                  <option value="">Choose a value domain…</option>
                  {domains.map((domain) => (
                    <option key={domain.key} value={domain.key}>{domain.label} ({domain.members.length})</option>
                  ))}
                </select>
                <span className="hint">A shared list, so two products offer the same answers.</span>
              </div>
            )}
          </div>

          {(isNumeric || isTextual) && (
            <fieldset className="fieldset">
              <legend>Limits</legend>
              <div className="field-row">
                {isNumeric && (
                  <>
                    <div className="field">
                      <label htmlFor="min">Smallest allowed</label>
                      <input id="min" className="control" type="number" value={validation.min}
                        onChange={(event) => setValidation({ ...validation, min: event.target.value })} />
                    </div>
                    <div className="field">
                      <label htmlFor="max">Largest allowed</label>
                      <input id="max" className="control" type="number" value={validation.max}
                        onChange={(event) => setValidation({ ...validation, max: event.target.value })} />
                    </div>
                    {dataType !== "integer" && (
                      <div className="field">
                        <label htmlFor="precision">Decimal places</label>
                        <input id="precision" className="control" type="number" min={0} max={8} value={validation.precision}
                          onChange={(event) => setValidation({ ...validation, precision: event.target.value })} />
                      </div>
                    )}
                  </>
                )}
                {isTextual && (
                  <>
                    <div className="field">
                      <label htmlFor="maxLength">Longest allowed</label>
                      <input id="maxLength" className="control" type="number" min={1} value={validation.maxLength}
                        onChange={(event) => setValidation({ ...validation, maxLength: event.target.value })} />
                    </div>
                    <div className="field">
                      <label htmlFor="pattern">Must match</label>
                      <input id="pattern" className="control" value={validation.pattern} placeholder="[A-Z]{2}"
                        onChange={(event) => setValidation({ ...validation, pattern: event.target.value })} />
                      <span className="hint">A regular expression. Anchored for you.</span>
                    </div>
                  </>
                )}
              </div>
              <p className="small muted" style={{ marginTop: 10 }}>
                A product may tighten these; it can never loosen them.
              </p>
            </fieldset>
          )}

          <div className="field">
            <label htmlFor="help">Help text</label>
            <textarea id="help" className="control" value={helpText} rows={2}
              onChange={(event) => setHelpText(event.target.value)}
              placeholder="Shown under the field on every form that asks it." />
          </div>

          {field && (
            <label className="checkline">
              <input type="checkbox" checked={active} onChange={(event) => setActive(event.target.checked)} />
              <span>
                Available for new products
                <div className="small muted">
                  Turn this off to retire the field. Products already asking for it keep working.
                </div>
              </span>
            </label>
          )}
        </div>

        <div className="card-foot">
          {field && !locked && (
            <>
              <button type="button" className="btn btn-danger" onClick={() => void remove()}>Delete</button>
              <div className="spacer" />
            </>
          )}
          <button type="button" className="btn" onClick={() => navigate("/fields")}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? "Saving…" : field ? "Save changes" : "Create field"}
          </button>
        </div>
      </form>
    </div>
  );
}
